import { useState, type ReactNode } from 'react';
import { Badge, Card, EventStatus, Icon, Modal } from '../../../shared/ui/Primitives';
import {
  demoStateFromSearch,
  readPage01Example,
  type ExampleEvent,
  type ExampleSuggestion,
} from '../adapters/demo-reader';
import { CapabilityChart, TrendChart } from './Charts';
import { EventTable } from './EventTable';
import { SuggestionPanel, WarningPanel } from './IntelligencePanels';
import { OverviewMap } from './OverviewMap';
import { Page01Header } from './OverviewPageHeader';
import { ReadinessPanels } from './ReadinessPanels';
import './overview.css';

type Detail =
  | { kind: 'event'; event: ExampleEvent }
  | { kind: 'suggestion'; suggestion: ExampleSuggestion }
  | { kind: 'notice'; title: string; body: string };

/** Read-only overview with explicitly labelled visual examples. */
export function Page01({ monitoringSummary }: { monitoringSummary?: ReactNode }) {
  const mode = demoStateFromSearch(window.location.search, import.meta.env.DEV);
  const read = readPage01Example(mode);
  const [selectedId, setSelectedId] = useState('demo-01');
  const [detail, setDetail] = useState<Detail | null>(null);
  const explain = (title: string, body: string) => setDetail({ kind: 'notice', title, body });
  const snapshot = read.status === 'read' ? read.snapshot : null;
  const selected = snapshot?.events.find((event) => event.id === selectedId);
  const eventDetail = (event: ExampleEvent) => {
    setSelectedId(event.id);
    setDetail({ kind: 'event', event });
  };
  return (
    <div className="p01">
      <Page01Header explain={explain} />
      <main id="p01-main" tabIndex={-1} className="p01-main">
        <p className="p01-muted">地图、态势卡片、事件与建议使用示例数据，非实时。</p>
        {!snapshot ? (
          <section className="p01-loading" aria-busy="true" aria-label="正在读取示范区域数据">
            <h2>正在读取示范区域数据…</h2>
            <p>Loading · 状态演示</p>
            <div className="p01-four-grid">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="p01-skeleton" />
              ))}
            </div>
            <div className="p01-skeleton p01-skeleton-map" />
            <p>加载完成前使用占位，不显示 0 条或 0%。</p>
          </section>
        ) : (
          <>
            {snapshot.freshness === 'stale' && (
              <div role="status" className="p01-stale">
                <Icon name="clock" />
                <div>
                  <strong>数据更新延迟 · 保留最后有效内容</strong>
                  <p>最后读取：{snapshot.lastRead}；距示例上下文时间延迟 42 分钟。</p>
                </div>
              </div>
            )}
            <div className="p01-hero-grid">
              <OverviewMap
                events={snapshot.events}
                selected={selected}
                select={setSelectedId}
                detail={() => {
                  if (selected) eventDetail(selected);
                }}
                explain={explain}
              />
              <Card
                zone="Z04"
                title="全域态势总览"
                className="p01-overview"
                extra={<span className="p01-muted">示例数据</span>}
              >
                <div className="p01-metrics">
                  {snapshot.data.metrics.map((metric, i) => (
                    <article key={metric.label} className={`p01-metric p01-${metric.color}`}>
                      <Icon name={metric.icon} />
                      <strong>{i === 0 ? snapshot.eventTotal : metric.value}</strong>
                      <h3>{metric.label}</h3>
                      <p>{metric.detail}</p>
                    </article>
                  ))}
                </div>
                <div className="p01-card-heading p01-events-heading">
                  <h2>重点事件</h2>
                  <a href="#Z10">
                    查看近期事件 <Icon name="arrow" />
                  </a>
                </div>
                <EventTable
                  events={snapshot.events}
                  selectedId={selected?.id}
                  select={setSelectedId}
                  detail={eventDetail}
                />
                <p className="p01-muted">显示 {snapshot.events.length} 条重点事件 · 示例数据</p>
              </Card>
            </div>
            <ReadinessPanels snapshot={snapshot} />
            <div className="p01-three-grid">
              <CapabilityChart snapshot={snapshot} />
              {monitoringSummary ?? (
                <WarningPanel
                  snapshot={snapshot}
                  retry={() => {
                    const result = readPage01Example(mode);
                    return result.status === 'read' ? result.snapshot.warnings : 'error';
                  }}
                />
              )}
              <SuggestionPanel
                snapshot={snapshot}
                show={(suggestion) => setDetail({ kind: 'suggestion', suggestion })}
              />
            </div>
            <div className="p01-three-grid p01-bottom-grid">
              <Card zone="Z10" title="近期事件">
                <EventTable
                  events={snapshot.events}
                  selectedId={selected?.id}
                  select={setSelectedId}
                  detail={eventDetail}
                  recent
                />
                <p className="p01-muted">
                  按发生时间倒序 · 示例 · 共展示 {snapshot.events.length} 条记录
                </p>
              </Card>
              <TrendChart snapshot={snapshot} />
              <Card zone="Z12" title="快捷入口">
                <div className="p01-shortcuts">
                  {snapshot.data.shortcuts.map((item) => (
                    <button
                      key={item.name}
                      onClick={() =>
                        explain(
                          item.name,
                          '该入口尚未开放。本轮仅提供只读查看，不创建记录、不执行资源调度、不下发动作。',
                        )
                      }
                    >
                      <Icon name={item.icon} />
                      <span>{item.name}</span>
                    </button>
                  ))}
                </div>
              </Card>
            </div>
          </>
        )}
      </main>
      <footer className="p01-footer" data-zone="Z12">
        <div>
          <span className="p01-brand-lockup">
            <img src="/page01/Brand_Lockup_Reference.png" alt="AI消防+救援 · AI Fire + Rescue" />
          </span>
          <span>守护更安全的城市与区域</span>
        </div>
        <div>
          {['关于我们', '隐私政策', '使用条款', '帮助中心'].map((name) => (
            <button key={name} onClick={() => explain(name, '本地演示尚未提供此页面。')}>
              {name}
            </button>
          ))}
          <span>v1.0.0（示例）</span>
        </div>
      </footer>
      {detail && (
        <Modal
          title={
            detail.kind === 'event'
              ? detail.event.name
              : detail.kind === 'suggestion'
                ? detail.suggestion.name
                : detail.title
          }
          close={() => setDetail(null)}
        >
          <p className="p01-authority">
            {detail.kind === 'suggestion'
              ? '辅助建议 · 不构成操作授权'
              : '只读研判 · 该读取结果不构成操作授权'}
          </p>
          {detail.kind === 'event' ? (
            <>
              <Badge>示例事件 · 非实时</Badge>
              <dl className="p01-detail-fields">
                {[
                  ['事件 ID', detail.event.id],
                  ['事件名称', detail.event.name],
                  ['位置', detail.event.location],
                  ['事件类型', detail.event.type],
                  ['发生时间（示例）', detail.event.time],
                  ['等级（示例）', detail.event.level],
                ].map(([label, value]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
                <div>
                  <dt>处置状态（示例）</dt>
                  <dd>
                    <EventStatus state={detail.event.state} />
                  </dd>
                </div>
              </dl>
              <p className="p01-muted">仅展示已有示例字段，未提供救援过程、调派记录或审批结果。</p>
            </>
          ) : detail.kind === 'suggestion' ? (
            <>
              <h3>建议摘要</h3>
              <p>{detail.suggestion.summary}</p>
              <h3>只读依据</h3>
              <p>交付数据未提供该建议的关联证据记录、来源链接、适用范围或更新时间。</p>
              <p className="p01-muted">待真实资料接入后核实。本面板不从事件名称推断关联依据。</p>
            </>
          ) : (
            <>
              <p>{detail.body}</p>
            </>
          )}
        </Modal>
      )}
    </div>
  );
}
